import type { AggregatedReactionDto } from '@splitfiesta/shared';
import { Button, Card } from '@splitfiesta/ui';

import { EmojiPicker } from '@/components/ui/EmojiPicker';
import { Portal } from '@/components/ui/Portal';

import { useReactionManagement } from '@/hooks/useReactionManagement';

import { resolveUserName } from '@/lib/displayName';

interface ReactionBarProps {
  reactions?: AggregatedReactionDto[];
  onAddReaction: (emoji: string) => void;
  onToggleReaction: (emoji: string, hasReacted: boolean) => void;
  loading?: boolean;
  currentUserId?: string;
  expenseId?: string; // Add expenseId for the hook
}

export function ReactionBar({
  reactions = [],
  onAddReaction,
  onToggleReaction,
  loading = false,
  currentUserId,
  expenseId = '',
}: ReactionBarProps) {
  // Use the extracted reaction management hook
  const {
    // State
    hoveredReaction,
    setHoveredReaction,
    tooltipPosition,
    tooltipRef,
    buttonRefs,

    // Computed values
    canAddNewEmoji,
    hoveredReactionData,

    // Styling helpers
    getExistingReactions,

    // Tooltip helpers
    getSortedReactionUsers,
    shouldShowMoreUsers,
    getMoreUsersCount,

    // Translation
    t,
  } = useReactionManagement({
    expenseId,
    reactions,
    currentUserId,
    loading,
  });

  return (
    <div
      className="flex flex-wrap items-center gap-2 relative"
      data-testid="reaction-bar"
    >
      {reactions.map((reaction, index) => {
        // Validate reaction object
        if (
          typeof reaction !== 'object' ||
          !reaction.emoji ||
          typeof reaction.emoji !== 'string'
        ) {
          return null;
        }
        return (
          <Button
            key={`${reaction.emoji}-${index}`}
            variant="ghost"
            size="sm"
            ref={el => {
              buttonRefs.current[reaction.emoji] = el;
            }}
            onClick={() =>
              !loading &&
              onToggleReaction(reaction.emoji, reaction.hasReacted || false)
            }
            onMouseEnter={() => setHoveredReaction(reaction.emoji)}
            onMouseLeave={() => setHoveredReaction(null)}
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full h-auto ${
              reaction.hasReacted
                ? 'bg-orange-100 dark:bg-orange-400/20 hover:bg-orange-200 dark:hover:bg-orange-400/30 ring-1 ring-orange-200 dark:ring-orange-400/50'
                : 'bg-[#F0EFED] dark:bg-gray-700 hover:bg-[#EBEAE8] dark:hover:bg-gray-600'
            }`}
            disabled={loading}
            data-testid={`reaction-${reaction.emoji}`}
          >
            <span className="text-base">{reaction.emoji || ''}</span>
            <span
              className={`text-xs font-medium ${
                reaction.hasReacted
                  ? 'text-orange-700 dark:text-orange-300'
                  : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              {reaction.count || 0}
            </span>
          </Button>
        );
      })}
      {canAddNewEmoji && (
        <EmojiPicker
          onSelect={onAddReaction}
          className="inline-flex"
          disabled={loading}
          existingReactions={getExistingReactions()}
        />
      )}

      {/* Reaction Users Tooltip */}
      {hoveredReaction &&
        hoveredReactionData?.users &&
        hoveredReactionData.users.length > 0 && (
          <Portal>
            <div
              ref={tooltipRef}
              className="fixed z-10000 pointer-events-none"
              style={{
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`,
                transform: 'translate(-50%, -100%)',
                maxWidth: '250px',
              }}
            >
              <Card className="p-3">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {t('reactions.reactedWith', {
                    emoji: hoveredReaction,
                  })}
                </div>
                <div className="space-y-0.5">
                  {getSortedReactionUsers(hoveredReactionData).map(
                    (user, userIndex) => (
                      <div
                        key={`${user.id || 'unknown'}-${userIndex}`}
                        className="text-sm text-gray-700 dark:text-gray-300"
                      >
                        {user.id === currentUserId ? (
                          <span className="font-medium">
                            {t('common:labels.you')}
                          </span>
                        ) : (
                          resolveUserName(user.name, t)
                        )}
                      </div>
                    )
                  )}
                  {shouldShowMoreUsers(hoveredReactionData) && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 font-medium mt-1.5 pt-1 border-t border-gray-100 dark:border-gray-700">
                      {getMoreUsersCount(hoveredReactionData) > 0
                        ? t('social:reactions.andXMore', {
                            count: getMoreUsersCount(hoveredReactionData),
                          })
                        : t('social:reactions.andMore')}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </Portal>
        )}
    </div>
  );
}
